import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { X, Send, Loader2, Mail, Check, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { documentsApi, customersApi, fileUrl } from "../lib/api";

/** Email confirmation modal for sending Estimates/Invoices.
 *  - Prefills `to` with the customer's email (override allowed).
 *  - Subject auto-derived from doc id; user can edit.
 *  - Optional custom intro message body.
 */
export default function EmailDocModal({ kind, doc, customer, payLink, acceptLink, onClose, onSent }) {
  const isEstimate = kind === "estimate";
  const label = isEstimate ? "Estimate" : "Invoice";
  const defaultSubject = `${label} ${doc.id}`;
  const [to, setTo] = useState(customer?.email || "");
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [photos, setPhotos] = useState(customer?.photos || []);
  const [selectedPhotoIds, setSelectedPhotoIds] = useState([]);
  const customerId = customer?.id || doc?.customerId;

  useEffect(() => {
    let active = true;
    if (customerId) {
      customersApi.get(customerId).then((c) => { if (active) setPhotos(c?.photos || []); }).catch(() => {});
    }
    return () => { active = false; };
  }, [customerId]);

  const togglePhoto = (id) =>
    setSelectedPhotoIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const includePayLink = !isEstimate && !!payLink && (doc.balanceDue || 0) > 0;
  const includeAcceptLink = isEstimate && !!acceptLink && doc.status !== "Converted";

  const validEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((s || "").trim());

  const send = async () => {
    if (!validEmail(to)) {
      toast.error("Please enter a valid recipient email.");
      return;
    }
    setSubmitting(true);
    try {
      const fn = isEstimate ? documentsApi.emailEstimate : documentsApi.emailInvoice;
      const res = await fn(doc.id, {
        to: to.trim(),
        subject: subject.trim() || defaultSubject,
        body: body.trim() || null,
        pay_link: includePayLink ? payLink : null,
        accept_link: includeAcceptLink ? acceptLink : null,
        photoIds: selectedPhotoIds,
      });
      if (res.sent) {
        if (res.autoStatusFlippedToSent) {
          toast.success(`Email sent · status moved to "Sent"`);
        } else {
          toast.success(`Email sent to ${to.trim()}`);
        }
        onSent?.(res);
        onClose();
      } else if (res.reason === "smtp_not_configured") {
        toast.error("Email isn't set up yet. Go to Company Settings → Email sending (SMTP) to connect your server.");
      } else {
        toast.error(res.reason || "Email failed");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Email failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/55 backdrop-blur-sm p-6"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-slate-200"
        data-testid="email-doc-modal"
      >
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-blue-600" />
            <div className="text-[14px] font-semibold text-slate-900">
              Email {label} to Customer
            </div>
          </div>
          <button
            onClick={onClose}
            className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center"
          >
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
              To
            </label>
            <input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="customer@example.com"
              data-testid="email-to"
              className={inputClass}
            />
            {!customer?.email && (
              <div className="mt-1 text-[11px] text-amber-700">
                No email on file for {customer?.name || "this customer"}. Enter one above.
              </div>
            )}
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
              Subject
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="email-subject"
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
              Message (optional)
            </label>
            <textarea
              rows={4}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={`Hi ${customer?.name || "there"},\n\nPlease find your ${label.toLowerCase()} attached.`}
              data-testid="email-body"
              className={`${inputClass} resize-none py-2`}
            />
            <div className="mt-1 text-[11px] text-slate-500">
              The {label.toLowerCase()} PDF will be attached automatically.
            </div>
          </div>
          {photos.length > 0 && (
            <div data-testid="email-photos-section">
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 flex items-center gap-1.5">
                  <ImageIcon className="h-3.5 w-3.5 text-violet-600" /> Attach photos
                </label>
                {selectedPhotoIds.length > 0 && (
                  <span className="text-[11px] font-semibold text-violet-700" data-testid="email-photos-count">
                    {selectedPhotoIds.length} selected
                  </span>
                )}
              </div>
              <div className="grid grid-cols-4 gap-2 max-h-40 overflow-y-auto pr-0.5" data-testid="email-photos-grid">
                {photos.map((p) => {
                  const sel = selectedPhotoIds.includes(p.id);
                  return (
                    <button
                      type="button"
                      key={p.id}
                      onClick={() => togglePhoto(p.id)}
                      data-testid={`email-photo-${p.id}`}
                      className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-colors ${sel ? "border-violet-500 ring-2 ring-violet-200" : "border-slate-200 hover:border-slate-300"}`}
                    >
                      <img
                        src={fileUrl(p.url)}
                        alt={p.caption || p.filename || "Customer photo"}
                        className="w-full h-full object-cover"
                      />
                      {sel && (
                        <div className="absolute inset-0 bg-violet-600/30 flex items-center justify-center">
                          <div className="h-5 w-5 rounded-full bg-violet-600 text-white flex items-center justify-center">
                            <Check className="h-3.5 w-3.5" strokeWidth={3} />
                          </div>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="mt-1 text-[11px] text-slate-500">
                Selected photos are attached to the email (up to 10).
              </div>
            </div>
          )}
          {isEstimate && doc.status === "Draft" && (
            <div className="px-3 py-2 rounded-md bg-blue-50 border border-blue-200 text-[12.5px] text-blue-800">
              Status will auto-advance from <strong>Draft</strong> to <strong>Sent</strong> once
              the email is delivered.
            </div>
          )}
          {includePayLink && (
            <div data-testid="email-pay-link-note" className="px-3 py-2 rounded-md bg-emerald-50 border border-emerald-200 text-[12.5px] text-emerald-800">
              A <strong>"Pay {`$${Number(doc.balanceDue || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`} online"</strong> button will be included so the customer can pay by card.
            </div>
          )}
          {includeAcceptLink && (
            <div data-testid="email-accept-link-note" className="px-3 py-2 rounded-md bg-blue-50 border border-blue-200 text-[12.5px] text-blue-800">
              A <strong>"Review &amp; sign your estimate"</strong> button will be included — the customer can accept, sign, and pay a deposit online.
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2 bg-slate-50/40">
          <Button variant="outline" onClick={onClose} className="h-9 text-[13px]">
            Cancel
          </Button>
          <Button
            onClick={send}
            disabled={submitting || !validEmail(to)}
            data-testid="email-send"
            className="h-9 bg-[#2563EB] hover:bg-[#1D4ED8] disabled:bg-slate-200 disabled:text-slate-500 text-white text-[13px] font-semibold"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Sending…
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-1.5" /> Send Email
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

const inputClass =
  "w-full h-9 px-2.5 rounded-md border border-slate-200 bg-white text-[13px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-colors";
